<script lang="ts">
import { shallowReactive } from 'vue';

// only the topmost modal shows its backdrop, it covers the modals below
const openModals = shallowReactive<object[]>([]);
</script>

<script setup lang="ts">
import 'bootstrap/scss/_modal.scss';
import 'bootstrap/scss/_close.scss';
import 'bootstrap/scss/_transitions.scss';
import { useHead } from '@unhead/vue';
import { computed, onBeforeUnmount, onMounted, onUnmounted, ref } from 'vue';
import { useRouter } from 'vue-router';

type Modifier =
  | 'dialog-drawer-left'
  | 'dialog-drawer-right'
  | 'dialog-centered'
  | 'dialog-scrollable'
  | 'sm'
  | 'lg'
  | 'xl'
  | 'fullscreen'
  | 'fullscreen-sm-down'
  | 'fullscreen-md-down'
  | 'fullscreen-lg-down'
  | 'fullscreen-xl-down'
  | 'fullscreen-xxl-down';

if (!import.meta.env.SSR && window.visualViewport) {
  const style = getComputedStyle(document.body);

  const delta =
    style.overflow === 'hidden'
      ? parseFloat(style.paddingRight) || 0
      : window.innerWidth - window.visualViewport.width;

  useHead({
    bodyAttrs: {
      style: `overflow:hidden;padding-right:${delta}px`,
    },
  });
}

const emit = defineEmits<{
  (e: 'hide'): void;
}>();

function hide() {
  emit('hide');
}

const props = withDefaults(
  defineProps<{
    title?: string;
    modifier?: Modifier | Modifier[];
    noClose?: boolean;
    guarded?: boolean;
    disabled?: boolean;
    onHide?: () => void;
    tag?: string;
  }>(),
  {
    title: undefined,
    modifier: undefined,
    onHide: undefined,
    tag: 'div',
  },
);

const shown = ref(false);

const modalKey = {};
const isTop = computed(() => openModals.at(-1) === modalKey);

const router = useRouter();

onMounted(() => {
  openModals.push(modalKey);

  setTimeout(() => {
    shown.value = true;
  }, 100);

  const beforeUnloadGuard = (ev: BeforeUnloadEvent) => {
    if (props.guarded && props.disabled) ev.preventDefault();
  };
  window.addEventListener('beforeunload', beforeUnloadGuard);
  onUnmounted(() =>
    window.removeEventListener('beforeunload', beforeUnloadGuard),
  );

  onUnmounted(
    router.beforeEach((_to, _from, next) => {
      if (!props.guarded || !props.onHide) {
        next();
        return;
      }

      if (!props.disabled) props.onHide();
      next(false);
    }),
  );
});

const el = ref<HTMLElement>();
onBeforeUnmount(() => {
  shown.value = false;

  const index = openModals.indexOf(modalKey);
  if (~index) openModals.splice(index, 1);

  if (el.value) {
    el.value.dataset.leaveDuration = '500';

    for (const child of el.value.children) {
      child.classList.remove('show');
    }
  }
});

const modifiers = computed(() => {
  const modifiers: Modifier[] = Array.isArray(props.modifier)
    ? props.modifier
    : [props.modifier || 'dialog-centered'];

  return modifiers.map((modifier) => `modal-${modifier}`);
});
</script>

<template>
  <div ref="el" :class="$style.root">
    <div class="modal-backdrop fade" :class="{ show: shown && isTop }"></div>

    <div
      class="modal fade"
      :class="{ show: shown }"
      tabindex="-1"
      style="display: block"
      @mousedown.stop.self="!disabled && !noClose && hide()"
      @touchstart.stop.self="!disabled && !noClose && hide()"
    >
      <div class="modal-dialog" :class="modifiers">
        <component :is="tag" class="modal-content">
          <div v-if="$slots.header" class="modal-header">
            <slot name="header" :hide="hide"></slot>
          </div>

          <div v-else-if="title" class="modal-header">
            <h2 class="modal-title fs-5">{{ title }}</h2>

            <button
              v-if="!!onHide && !noClose"
              type="button"
              class="btn-close"
              :aria-label="$t`Close`"
              :disabled="disabled"
              @click="hide"
            ></button>
          </div>

          <button
            v-else-if="!!onHide && !noClose"
            type="button"
            class="btn-close"
            :aria-label="$t`Close`"
            :disabled="disabled"
            @click="hide"
          ></button>

          <div class="modal-body">
            <slot :hide="hide"></slot>
          </div>

          <div v-if="$slots.footer" class="modal-footer">
            <slot name="footer" :hide="hide"></slot>
          </div>
        </component>
      </div>
    </div>
  </div>
</template>

<style lang="scss" module>
.root {
  position: absolute;

  // each modal is its own stacking context, so a later modal's backdrop
  // covers an earlier modal's dialog
  z-index: $zindex-modal-backdrop;

  :global {
    .modal {
      overflow: hidden;

      --#{$prefix}modal-border-color: transparent;
      --#{$prefix}modal-color: var(--#{$prefix}body-color);
    }

    .modal-content > .btn-close {
      position: absolute;
      z-index: 1;
      right: calc(var(--#{$prefix}modal-header-padding-x) * 0.5);
      top: calc(var(--#{$prefix}modal-header-padding-y) * 0.5);
      padding: calc(var(--#{$prefix}modal-header-padding-y) * 0.5)
        calc(var(--#{$prefix}modal-header-padding-x) * 0.5);
    }

    .modal-dialog {
      --#{$prefix}modal-fade-transform: #{$modal-fade-transform};

      transition-duration: 0.15s;
    }

    .modal.fade:not(.show) .modal-dialog {
      transform: var(--#{$prefix}modal-fade-transform);
    }

    .modal.fade .modal-dialog {
      transition-property: transform, max-width;
    }

    .modal-dialog-drawer-left,
    .modal-dialog-right {
      display: flex;
      align-items: stretch;
      min-height: 100%;

      --#{$prefix}modal-margin: 0px;

      margin-left: 0;

      .modal-content {
        border-radius: 0;
        border-bottom: none;
        border-top: none;
      }
    }

    .modal-dialog-drawer-left {
      --#{$prefix}modal-fade-transform: translate(
        calc(var(--#{$prefix}modal-width) * -1),
        0
      );
    }

    .modal-dialog-right {
      --#{$prefix}modal-fade-transform: translate(
        var(--#{$prefix}modal-width),
        0
      );
    }
  }
}
</style>
